import { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  Copy,
  ChevronDown,
  ChevronRight,
  Play,
  RotateCcw,
  Loader2,
  CheckCircle2,
  XCircle,
  Zap,
} from 'lucide-react';
import { useAuth } from '@shared/context/useAuth';
import { getAccessToken, type TokenMeta } from '@shared/services/authService';
import apiClient from '@shared/services/apiClient';
import './SecurityTab.css';

// ─── Types ───

interface EndpointPermission {
  method: string;
  path: string;
  policy: string;
  description: string;
}

interface TestResult {
  status: number;
  passed: boolean;
}

// ─── Helpers ───

function formatRelativeTime(expiresAtUtc: string | null): string {
  if (!expiresAtUtc) return 'Never';
  const diff = new Date(expiresAtUtc).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  if (hours > 0) return `${hours}h ${minutes}m remaining`;
  return `${minutes}m remaining`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleString();
}

function groupEndpoints(
  endpoints: EndpointPermission[]
): Record<string, EndpointPermission[]> {
  const groups: Record<string, EndpointPermission[]> = {};
  for (const ep of endpoints) {
    // Derive group from path: /api/clinics/... → Clinics
    const parts = ep.path.split('/').filter(Boolean); // ['api', 'clinics', ...]
    let group = parts[1] ?? 'Other';
    // Capitalize
    group = group.charAt(0).toUpperCase() + group.slice(1);
    // Normalize compound paths
    if (group === 'Clinic-hierarchy') group = 'Hierarchy';
    if (!groups[group]) groups[group] = [];
    groups[group].push(ep);
  }
  return groups;
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'st-method-get',
  POST: 'st-method-post',
  PUT: 'st-method-put',
  DELETE: 'st-method-delete',
  PATCH: 'st-method-patch',
};

// ─── Dev Token Generator Config ───

const AVAILABLE_ROLES = [
  'Admin',
  'Clinician',
  'Manager',
  'ReadOnly',
  'Scheduler',
  'BillingAdmin',
  'FieldTech',
];

const EXPIRATION_OPTIONS = [
  { label: '1 hour', minutes: 60 },
  { label: '8 hours', minutes: 480 },
  { label: '24 hours', minutes: 1440 },
  { label: '7 days', minutes: 10080 },
];

interface GeneratedToken {
  token: string;
  meta: TokenMeta;
}

// ─── Component ───

export default function SecurityTab() {
  const { user, setToken } = useAuth();
  const rawToken = getAccessToken();

  // Section A state
  const [showRawToken, setShowRawToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expiryText, setExpiryText] = useState('');

  // Section B state
  const [endpoints, setEndpoints] = useState<EndpointPermission[]>([]);
  const [loadingEndpoints, setLoadingEndpoints] = useState(false);
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [testingKey, setTestingKey] = useState<string | null>(null);
  const [testingAll, setTestingAll] = useState(false);

  // Section C state (dev token generator)
  const [genUserId, setGenUserId] = useState(1);
  const [genEmail, setGenEmail] = useState('dev@examinetics.com');
  const [genName, setGenName] = useState('Dev User');
  const [genRoles, setGenRoles] = useState<string[]>(['Admin']);
  const [genExpiration, setGenExpiration] = useState(480);
  const [generating, setGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<GeneratedToken | null>(null);
  const [genCopied, setGenCopied] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  // Refresh expiry countdown every 30s
  useEffect(() => {
    if (!user) return;
    const update = () => setExpiryText(formatRelativeTime(user.expiresAtUtc));
    update();
    const interval = setInterval(update, 30_000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch endpoint list on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchEndpoints() {
      setLoadingEndpoints(true);
      try {
        const res = await apiClient.get<{ data: { endpoints: EndpointPermission[] } }>(
          '/dev/permissions'
        );
        if (!cancelled) setEndpoints(res.data.data.endpoints);
      } catch {
        // Fallback: empty list — endpoint may not be available in non-dev
      } finally {
        if (!cancelled) setLoadingEndpoints(false);
      }
    }
    fetchEndpoints();
    return () => { cancelled = true; };
  }, []);

  const handleCopyToken = useCallback(async () => {
    if (!rawToken) return;
    await navigator.clipboard.writeText(rawToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [rawToken]);

  const endpointKey = (ep: EndpointPermission) => `${ep.method}:${ep.path}`;

  const testEndpoint = useCallback(
    async (ep: EndpointPermission) => {
      const key = endpointKey(ep);
      setTestingKey(key);
      try {
        const method = ep.method.toLowerCase() as 'get' | 'post' | 'put' | 'delete' | 'patch';
        // For write methods, send empty body — we expect 400 (validation) not 401
        const body = method === 'get' || method === 'delete' ? undefined : {};

        const response = await apiClient.request({
          method,
          url: ep.path.replace('/api', ''),
          data: body,
          // Don't let the 401 interceptor redirect us during permission testing
          validateStatus: () => true,
        });

        setResults((prev) => ({
          ...prev,
          [key]: {
            status: response.status,
            passed: response.status !== 401 && response.status !== 403,
          },
        }));
      } catch {
        // Network error or interceptor rejection
        setResults((prev) => ({
          ...prev,
          [key]: { status: 0, passed: false },
        }));
      } finally {
        setTestingKey(null);
      }
    },
    []
  );

  const testAll = useCallback(async () => {
    setTestingAll(true);
    setResults({});
    for (const ep of endpoints) {
      await testEndpoint(ep);
      // Small delay to avoid overwhelming the API
      await new Promise((r) => setTimeout(r, 100));
    }
    setTestingAll(false);
  }, [endpoints, testEndpoint]);

  const clearResults = () => setResults({});

  // Section C: Dev Token Generator handlers
  const toggleRole = (role: string) => {
    setGenRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const generateToken = useCallback(
    async (overrideRoles?: string[]) => {
      setGenerating(true);
      setGenError(null);
      setGeneratedToken(null);
      try {
        const body = {
          userId: genUserId,
          email: genEmail,
          displayName: genName,
          roles: overrideRoles ?? genRoles,
          expirationMinutes: genExpiration,
        };
        const res = await apiClient.post('/dev/token', body, {
          validateStatus: () => true,
        });
        if (res.status !== 200) {
          setGenError(`API returned ${res.status}: ${JSON.stringify(res.data)}`);
          return;
        }
        const data = res.data as {
          token: string;
          userId: number;
          email: string;
          displayName: string;
          roles: string[];
          issuedAtUtc: string;
          expiresAtUtc: string | null;
        };
        setGeneratedToken({
          token: data.token,
          meta: {
            userId: data.userId,
            email: data.email,
            displayName: data.displayName,
            roles: data.roles,
            issuedAtUtc: data.issuedAtUtc,
            expiresAtUtc: data.expiresAtUtc,
          },
        });
      } catch (err) {
        setGenError(err instanceof Error ? err.message : 'Failed to generate token');
      } finally {
        setGenerating(false);
      }
    },
    [genUserId, genEmail, genName, genRoles, genExpiration]
  );

  const handleCopyGenerated = useCallback(async () => {
    if (!generatedToken) return;
    await navigator.clipboard.writeText(generatedToken.token);
    setGenCopied(true);
    setTimeout(() => setGenCopied(false), 2000);
  }, [generatedToken]);

  const handleUseToken = useCallback(() => {
    if (!generatedToken) return;
    setToken(generatedToken.token, generatedToken.meta);
  }, [generatedToken, setToken]);

  const grouped = groupEndpoints(endpoints);
  const totalTested = Object.keys(results).length;
  const totalPassed = Object.values(results).filter((r) => r.passed).length;
  const totalFailed = totalTested - totalPassed;

  // ─── Render ───

  return (
    <div className="st">
      {/* Section A: Current User Info */}
      <div className="st-section">
        <div className="st-section-header">
          <Shield size={18} />
          <h3 className="st-section-title">Current User</h3>
        </div>

        {!user ? (
          <div className="st-empty">Not authenticated. Please log in.</div>
        ) : (
          <>
            <div className="st-info-grid">
              <div className="st-info-item">
                <span className="st-info-label">User ID</span>
                <span className="st-info-value">{user.userId}</span>
              </div>
              <div className="st-info-item">
                <span className="st-info-label">Email</span>
                <span className="st-info-value">{user.email}</span>
              </div>
              <div className="st-info-item">
                <span className="st-info-label">Name</span>
                <span className="st-info-value">{user.displayName}</span>
              </div>
              <div className="st-info-item">
                <span className="st-info-label">Auth Scheme</span>
                <span className="st-info-value">Legacy Token</span>
              </div>
            </div>

            {user.roles.length > 0 && (
              <div className="st-roles">
                <span className="st-info-label">Roles</span>
                <div className="st-role-list">
                  {user.roles.map((role) => (
                    <span key={role} className="st-role-badge">{role}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="st-info-grid st-info-grid-dates">
              <div className="st-info-item">
                <span className="st-info-label">Issued At</span>
                <span className="st-info-value">{formatDateTime(user.issuedAtUtc)}</span>
              </div>
              <div className="st-info-item">
                <span className="st-info-label">Expires At</span>
                <span className="st-info-value">
                  {formatDateTime(user.expiresAtUtc)}
                  {user.expiresAtUtc && (
                    <span className={`st-expiry-badge ${expiryText === 'Expired' ? 'expired' : ''}`}>
                      {expiryText}
                    </span>
                  )}
                </span>
              </div>
            </div>

            {rawToken && (
              <div className="st-raw-token">
                <button
                  type="button"
                  className="st-raw-token-toggle"
                  onClick={() => setShowRawToken(!showRawToken)}
                >
                  {showRawToken ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Raw Token
                </button>
                {showRawToken && (
                  <div className="st-raw-token-content">
                    <pre className="st-raw-token-text">{rawToken}</pre>
                    <button type="button" className="st-copy-btn" onClick={handleCopyToken}>
                      <Copy size={14} />
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Section B: Permission Tester */}
      <div className="st-section">
        <div className="st-section-header">
          <Play size={18} />
          <h3 className="st-section-title">Permission Tester</h3>
          <div className="st-section-actions">
            <button
              type="button"
              className="st-btn st-btn-primary"
              onClick={testAll}
              disabled={testingAll || endpoints.length === 0}
            >
              {testingAll ? <Loader2 size={14} className="st-spin" /> : <Play size={14} />}
              Test All
            </button>
            <button
              type="button"
              className="st-btn st-btn-secondary"
              onClick={clearResults}
              disabled={totalTested === 0}
            >
              <RotateCcw size={14} />
              Clear
            </button>
          </div>
        </div>

        {loadingEndpoints ? (
          <div className="st-loading">
            <Loader2 size={20} className="st-spin" />
            Loading endpoints...
          </div>
        ) : endpoints.length === 0 ? (
          <div className="st-empty">
            No endpoints available. The dev permissions endpoint may not be accessible.
          </div>
        ) : (
          <>
            {Object.entries(grouped).map(([group, eps]) => (
              <div key={group} className="st-endpoint-group">
                <h4 className="st-group-name">{group}</h4>
                <div className="st-endpoint-table">
                  {eps.map((ep) => {
                    const key = endpointKey(ep);
                    const result = results[key];
                    const isTesting = testingKey === key;
                    return (
                      <div key={key} className="st-endpoint-row">
                        <span className={`st-method ${METHOD_COLORS[ep.method] ?? ''}`}>
                          {ep.method}
                        </span>
                        <span className="st-path">{ep.path}</span>
                        <span className="st-description">{ep.description}</span>
                        <button
                          type="button"
                          className="st-btn st-btn-sm"
                          onClick={() => testEndpoint(ep)}
                          disabled={isTesting || testingAll}
                        >
                          {isTesting ? (
                            <Loader2 size={12} className="st-spin" />
                          ) : (
                            'Test'
                          )}
                        </button>
                        <span className="st-result">
                          {result && (
                            <>
                              {result.passed ? (
                                <CheckCircle2 size={16} className="st-result-pass" />
                              ) : (
                                <XCircle size={16} className="st-result-fail" />
                              )}
                              <span className={result.passed ? 'st-result-pass' : 'st-result-fail'}>
                                {result.status || 'ERR'}
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {totalTested > 0 && (
              <div className="st-summary">
                <strong>{totalPassed}</strong>/{totalTested} passed
                {totalFailed > 0 && (
                  <span className="st-summary-failed">, <strong>{totalFailed}</strong> failed</span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Section C: Dev Token Generator (Development Only) */}
      {import.meta.env.DEV && (
        <div className="st-section">
          <div className="st-section-header">
            <Zap size={18} />
            <h3 className="st-section-title">Dev Token Generator</h3>
            <span className="st-dev-badge">Development Only</span>
          </div>

          {/* Quick Presets */}
          <div className="st-presets">
            <span className="st-info-label">Quick Presets</span>
            <div className="st-preset-buttons">
              <button
                type="button"
                className="st-btn st-btn-preset"
                onClick={() => generateToken(['Admin'])}
                disabled={generating}
              >
                Admin
              </button>
              <button
                type="button"
                className="st-btn st-btn-preset"
                onClick={() => generateToken(['ReadOnly'])}
                disabled={generating}
              >
                ReadOnly
              </button>
              <button
                type="button"
                className="st-btn st-btn-preset st-btn-preset-danger"
                onClick={() => generateToken([])}
                disabled={generating}
              >
                No Roles
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="st-gen-form">
            <div className="st-gen-row">
              <div className="st-gen-field">
                <label className="st-info-label" htmlFor="gen-user-id">User ID</label>
                <input
                  id="gen-user-id"
                  type="number"
                  className="st-gen-input st-gen-input-narrow"
                  value={genUserId}
                  onChange={(e) => setGenUserId(Number(e.target.value))}
                />
              </div>
              <div className="st-gen-field st-gen-field-grow">
                <label className="st-info-label" htmlFor="gen-email">Email</label>
                <input
                  id="gen-email"
                  type="text"
                  className="st-gen-input"
                  value={genEmail}
                  onChange={(e) => setGenEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="st-gen-row">
              <div className="st-gen-field st-gen-field-grow">
                <label className="st-info-label" htmlFor="gen-name">Display Name</label>
                <input
                  id="gen-name"
                  type="text"
                  className="st-gen-input"
                  value={genName}
                  onChange={(e) => setGenName(e.target.value)}
                />
              </div>
              <div className="st-gen-field">
                <label className="st-info-label" htmlFor="gen-expiration">Expiration</label>
                <select
                  id="gen-expiration"
                  className="st-gen-input"
                  value={genExpiration}
                  onChange={(e) => setGenExpiration(Number(e.target.value))}
                >
                  {EXPIRATION_OPTIONS.map((opt) => (
                    <option key={opt.minutes} value={opt.minutes}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="st-gen-field">
              <span className="st-info-label">Roles</span>
              <div className="st-gen-roles">
                {AVAILABLE_ROLES.map((role) => (
                  <label key={role} className="st-gen-role-check">
                    <input
                      type="checkbox"
                      checked={genRoles.includes(role)}
                      onChange={() => toggleRole(role)}
                    />
                    <span>{role}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="st-btn st-btn-primary"
              onClick={() => generateToken()}
              disabled={generating}
            >
              {generating ? <Loader2 size={14} className="st-spin" /> : <Zap size={14} />}
              Generate Token
            </button>
          </div>

          {/* Error */}
          {genError && (
            <div className="st-gen-error">
              <XCircle size={14} />
              {genError}
            </div>
          )}

          {/* Generated Token Output */}
          {generatedToken && (
            <div className="st-gen-output">
              <div className="st-gen-output-header">
                <span className="st-info-label">Generated Token</span>
                <div className="st-section-actions">
                  <button
                    type="button"
                    className="st-btn st-btn-sm"
                    onClick={handleCopyGenerated}
                  >
                    <Copy size={12} />
                    {genCopied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    className="st-btn st-btn-primary st-btn-sm"
                    onClick={handleUseToken}
                  >
                    Use This Token
                  </button>
                </div>
              </div>
              <pre className="st-raw-token-text">{generatedToken.token}</pre>
              <div className="st-gen-output-meta">
                <span>User: {generatedToken.meta.displayName} ({generatedToken.meta.email})</span>
                <span>Roles: {generatedToken.meta.roles.length > 0 ? generatedToken.meta.roles.join(', ') : 'None'}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}